import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getProduct } from "../services/api";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { getProductImage, getProductImages } from "../utils/imageHelper";
import ImageLightbox from "../components/ImageLightbox";
import guideTailleImage from "../assets/images/guide-taille.png";
import "./ProductDetail.css";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { storeClosed, storeClosedMessage } = useStore();
  const [addedToCart, setAddedToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await getProduct(id);
        setProduct(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(
            response.data.sizes[0].size || response.data.sizes[0]
          );
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error("Erreur lors du chargement du produit:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);

  // Obtenir le stock de la taille sélectionnée
  const getStockForSize = (size) => {
    if (!product || !product.sizes) return 0;

    // Si sizes est un tableau d'objets avec {size, stock}
    const sizeObj = product.sizes.find((s) => s.size === size);
    if (sizeObj && sizeObj.stock !== undefined) {
      return sizeObj.stock;
    }

    // Ancien format : sizes est un tableau de strings
    return product.stock || 0;
  };

  const currentStock = getStockForSize(selectedSize);

  // Lightbox handlers
  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Slider d'images - Logique propre et optimisée
  const allImages = product?.images || [];
  const totalImages = allImages.length;
  const imageUrls = getProductImages(product?.images);

  const goToNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === totalImages - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? totalImages - 1 : prevIndex - 1
    );
  };

  const goToSpecificImage = (index) => {
    if (index >= 0 && index < totalImages) {
      setCurrentImageIndex(index);
    }
  };

  // Gestion des événements tactiles (swipe)
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        // Swipe vers la gauche -> image suivante
        goToNextImage();
      } else {
        // Swipe vers la droite -> image précédente
        goToPreviousImage();
      }
    }

    setTouchStartX(null);
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Veuillez sélectionner une taille");
      return;
    }

    if (currentStock === 0) {
      alert("Désolé, cette taille est en rupture de stock");
      return;
    }

    if (quantity > currentStock) {
      alert(
        `Désolé, seulement ${currentStock} article(s) disponible(s) pour cette taille`
      );
      return;
    }

    addToCart(product, selectedSize, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (!product) {
    return <div className="error">Produit non trouvé</div>;
  }

  return (
    <div className="product-detail">
      <div className="container">
        <div className="product-layout">
          <div className="product-images">
            {/* Desktop: Grid d'images */}
            <div className="images-grid desktop-only">
              {product.images?.slice(0, 2).map((image, index) => (
                <img
                  key={index}
                  src={getProductImage(image)}
                  alt={`${product.name} ${index + 1}`}
                  className="grid-image clickable-image"
                  onClick={() => openLightbox(index)}
                />
              ))}
            </div>
            {product.images?.[2] && (
              <img
                src={getProductImage(product.images[2])}
                alt={`${product.name} 3`}
                className="full-image desktop-only clickable-image"
                onClick={() => openLightbox(2)}
              />
            )}

            {/* Mobile: Slider d'images */}
            <div
              className="image-slider mobile-only"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div className="slider-container">
                <div
                  className="slider-track"
                  style={{
                    transform: `translateX(-${currentImageIndex * 100}%)`,
                  }}
                >
                  {allImages.map((image, index) => (
                    <div key={index} className="slider-image-wrapper">
                      <img
                        src={getProductImage(image)}
                        alt={`${product.name} ${index + 1}`}
                        className="slider-image"
                        onClick={() => openLightbox(index)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation buttons */}
              {allImages.length > 1 && (
                <>
                  <button
                    className="slider-btn slider-btn-prev"
                    onClick={goToPreviousImage}
                    aria-label="Image précédente"
                    type="button"
                  >
                    <span>‹</span>
                  </button>
                  <button
                    className="slider-btn slider-btn-next"
                    onClick={goToNextImage}
                    aria-label="Image suivante"
                    type="button"
                  >
                    <span>›</span>
                  </button>

                  {/* Dots indicator */}
                  <div className="slider-dots">
                    {allImages.map((_, index) => (
                      <button
                        key={index}
                        className={`slider-dot ${
                          index === currentImageIndex ? "active" : ""
                        }`}
                        onClick={() => goToSpecificImage(index)}
                        aria-label={`Image ${index + 1}`}
                        type="button"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="product-details">
            <h1 className="product-title">{product.name}</h1>
            <p className="product-category">{product.category}</p>
            <p className="product-price">{product.price.toFixed(2)}€</p>
            <p className="shipping-note">
              * Frais de livraison calculés au moment de la commande. Retrait
              gratuit en boutique : CrossFit Claye ou CrossFit Diversity.
            </p>

            {currentStock <= 10 && currentStock > 0 && (
              <div className="stock-alert">
                <span className="alert-icon">⚠️</span>
                <span className="alert-text">
                  Plus que{" "}
                  <strong>
                    {currentStock} article{currentStock > 1 ? "s" : ""}
                  </strong>{" "}
                  en stock pour cette taille !
                </span>
              </div>
            )}

            {currentStock === 0 && selectedSize && (
              <div className="out-of-stock-alert">
                <span className="alert-icon">❌</span>
                <span className="alert-text">
                  Rupture de stock pour cette taille
                </span>
              </div>
            )}

            <p className="product-description">{product.description}</p>

            <div className="product-options">
              <div className="option-group">
                <div className="size-label-wrapper">
                  <label>Taille</label>
                  <button 
                    className="size-guide-toggle"
                    onClick={() => setShowSizeGuide(!showSizeGuide)}
                    type="button"
                  >
                    📏 Guide des tailles
                  </button>
                </div>
                <div className="size-selector">
                  {product.sizes?.map((sizeObj) => {
                    const sizeName = sizeObj.size || sizeObj;
                    const sizeStock = getStockForSize(sizeName);
                    return (
                      <button
                        key={sizeName}
                        className={`size-btn ${
                          selectedSize === sizeName ? "active" : ""
                        } ${sizeStock === 0 ? "out-of-stock" : ""}`}
                        onClick={() => setSelectedSize(sizeName)}
                        disabled={sizeStock === 0}
                      >
                        {sizeName}
                      </button>
                    );
                  })}
                </div>

                {/* Guide des tailles collapsible */}
                {showSizeGuide && (
                  <div className="size-guide-dropdown">
                    <div className="size-guide-content">
                      <img 
                        src={guideTailleImage} 
                        alt="Guide des tailles" 
                        className="size-guide-image"
                      />
                      <table className="size-guide-table-compact">
                        <tbody>
                          <tr>
                            <td className="label-cell">Taille</td>
                            <td><strong>XS</strong></td>
                            <td><strong>S</strong></td>
                            <td><strong>M</strong></td>
                            <td><strong>L</strong></td>
                            <td><strong>XL</strong></td>
                          </tr>
                          <tr>
                            <td className="label-cell">A</td>
                            <td>53</td>
                            <td>56</td>
                            <td>59</td>
                            <td>62</td>
                            <td>65</td>
                          </tr>
                          <tr>
                            <td className="label-cell">B</td>
                            <td>72</td>
                            <td>74</td>
                            <td>76</td>
                            <td>78</td>
                            <td>80</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="option-group">
                <label>Quantité</label>
                <div className="quantity-selector">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </button>
                  <span>{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)}>+</button>
                </div>
              </div>
            </div>

            {storeClosed || product.purchaseDisabled ? (
              <div className="purchase-disabled-banner">
                <span className="purchase-disabled-icon">🚫</span>
                <span className="purchase-disabled-text">
                  {(product.purchaseDisabled && product.purchaseDisabledMessage)
                    ? product.purchaseDisabledMessage
                    : (storeClosed && storeClosedMessage)
                    ? storeClosedMessage
                    : "Ce produit est temporairement indisponible à la vente."}
                </span>
              </div>
            ) : (
              <button
                className={`btn btn-primary add-to-cart ${
                  addedToCart ? "added" : ""
                }`}
                onClick={handleAddToCart}
              >
                {addedToCart ? "✓ Ajouté au panier" : "Ajouter au panier"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox carrousel plein écran */}
      <ImageLightbox
        images={imageUrls}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
      />
    </div>
  );
};

export default ProductDetail;
